import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import googleSheetsService from '../services/googleSheetsService';

const Contact = () => {
  const form = useRef();
  const [formData, setFormData] = useState({
    from_name: '',
    from_email: '',
    phone_number: '',
    company_name: '',
    service_interest: '',
    message: ''
  });

  const [formStatus, setFormStatus] = useState({
    submitted: false,
    error: false,
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Set loading state
    setFormStatus({
      submitted: false,
      error: false,
      message: 'Sending your message...'
    });

    // Create a submission object with timestamp
    const submission = {
      ...formData,
      timestamp: new Date().toISOString()
    };

    // Store in localStorage for admin access
    const existingInquiries = JSON.parse(localStorage.getItem('inquiries') || '[]');
    existingInquiries.push(submission);
    localStorage.setItem('inquiries', JSON.stringify(existingInquiries));

    // Also update Google Sheets
    const sheetsResult = await googleSheetsService.appendInquiry(formData);
    if (sheetsResult.success) {
      console.log('Successfully updated Google Sheets');
    } else {
      console.warn('Failed to update Google Sheets:', sheetsResult.error);
    }

    // Send the form data using EmailJS with clearer error reporting
    try {
      const result = await emailjs.sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        e.target, // ensure we pass the actual HTMLFormElement
        { publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY }
      );
      console.log('SUCCESS!', result);
      setFormStatus({
        submitted: true,
        error: false,
        message: 'Your message has been sent successfully and added to our records!'
      });
      // Reset form data
      setFormData({
        from_name: '',
        from_email: '',
        phone_number: '',
        company_name: '',
        service_interest: '',
        message: ''
      });
    } catch (error) {
      console.error('EmailJS send failed', error);
      const detail = error?.text || error?.message || 'Unknown error';
      setFormStatus({
        submitted: false,
        error: true,
        message: `Failed to send email notification (${detail}). Your inquiry was saved.`
      });
    }
  };

  return (
    <>
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden min-h-[60vh] flex items-center bg-dark">
        <div className="absolute inset-0 z-0">
          {/* Abstract Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-black opacity-90"></div>
          <div className="absolute top-0 right-0 w-1/2 h-full bg-[#FF914D] opacity-10 blur-3xl rounded-full transform translate-x-1/3"></div>
          <div className="absolute bottom-0 left-0 w-1/2 h-full bg-[#FF914D] opacity-10 blur-3xl rounded-full transform -translate-x-1/3"></div>
        </div>

        <div className="container relative z-10 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-3xl mx-auto"
          >
            <h1 className="mb-6 text-5xl font-bold md:text-7xl font-display">
              Let's <span className="text-[#FF914D]">Talk</span>
            </h1>
            <p className="text-xl text-gray-300 font-light max-w-2xl mx-auto">
              Ready to crisp up your content? Whether you have a question or you're ready to start, we're all ears.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-20 bg-gray-50 -mt-20 relative z-20">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="space-y-10 lg:pr-12 pt-10"
            >
              <div>
                <h2 className="text-3xl font-bold font-display text-dark mb-6">Get in Touch</h2>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Fill out the form and our team will get back to you within 24 hours. We're excited to learn about your business and how we can help you achieve your goals.
                </p>
              </div>

              <div className="space-y-8">
                <div className="flex items-start group">
                  <div className="flex-shrink-0 p-4 mr-6 text-white bg-[#FF914D] rounded-2xl shadow-lg shadow-orange-200 group-hover:scale-110 transition-transform duration-300">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="mb-1 text-xl font-bold text-dark">Email</h3>
                    <p className="text-gray-600 text-lg">socialcroutonofficial@gmail.com</p>
                  </div>
                </div>

                <div className="flex items-start group">
                  <div className="flex-shrink-0 p-4 mr-6 text-white bg-[#FF914D] rounded-2xl shadow-lg shadow-orange-200 group-hover:scale-110 transition-transform duration-300">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="mb-1 text-xl font-bold text-dark">Phone</h3>
                    <p className="text-gray-600 text-lg">+91 9325231622</p>
                  </div>
                </div>

                <div className="flex items-start group">
                  <div className="flex-shrink-0 p-4 mr-6 text-white bg-[#FF914D] rounded-2xl shadow-lg shadow-orange-200 group-hover:scale-110 transition-transform duration-300">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="mb-1 text-xl font-bold text-dark">Office</h3>
                    <p className="text-gray-600 text-lg">308, Mont Vert Apex, Baner Main Road, Pune- 411045</p>
                  </div>
                </div>
              </div>

              <div className="pt-8 border-t border-gray-200">
                <h3 className="mb-6 text-xl font-bold text-dark">Follow Us</h3>
                <div className="flex space-x-4">
                  <a href="https://www.instagram.com/socialcrouton" target="_blank" rel="noopener noreferrer" className="p-3 text-white bg-dark rounded-full hover:bg-[#FF914D] transition-colors duration-300">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
                    </svg>
                  </a>
                  <a href="https://www.linkedin.com/company/social-crouton" target="_blank" rel="noopener noreferrer" className="p-3 text-white bg-dark rounded-full hover:bg-[#FF914D] transition-colors duration-300">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path fillRule="evenodd" d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" clipRule="evenodd" />
                    </svg>
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl shadow-2xl p-8 md:p-10 border border-gray-100 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF914D] opacity-5 rounded-bl-full"></div>

              {formStatus.submitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="mb-4 text-2xl font-bold text-dark">Message Sent!</h3>
                  <p className="text-gray-600 text-lg">
                    {formStatus.message || "Thanks for reaching out. We'll get back to you shortly!"}
                  </p>
                  <button
                    onClick={() => setFormStatus({ submitted: false, error: false, message: '' })}
                    className="mt-8 text-[#FF914D] font-semibold hover:underline"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="text-2xl font-bold text-dark mb-8">Send us a Message</h3>

                  {formStatus.error && (
                    <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg border border-red-100">
                      {formStatus.message || "Something went wrong. Please try again."}
                    </div>
                  )}

                  {formStatus.message && !formStatus.submitted && !formStatus.error && (
                    <div className="mb-6 p-4 bg-blue-50 text-blue-700 rounded-lg border border-blue-100 flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {formStatus.message}
                    </div>
                  )}

                  <form ref={form} onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                      <div>
                        <label htmlFor="name" className="block mb-2 text-sm font-medium text-gray-700">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="from_name"
                          value={formData.from_name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF914D] focus:border-transparent transition-all"
                          placeholder="John Doe"
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-700">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="from_email"
                          value={formData.from_email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF914D] focus:border-transparent transition-all"
                          placeholder="john@example.com"
                        />
                      </div>

                      <div>
                        <label htmlFor="phone" className="block mb-2 text-sm font-medium text-gray-700">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone_number"
                          value={formData.phone_number}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF914D] focus:border-transparent transition-all"
                          placeholder="9876543210"
                        />
                      </div>

                      <div>
                        <label htmlFor="company" className="block mb-2 text-sm font-medium text-gray-700">
                          Company Name
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company_name"
                          value={formData.company_name}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF914D] focus:border-transparent transition-all"
                          placeholder="Your Company"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="service" className="block mb-2 text-sm font-medium text-gray-700">
                        Service You're Interested In *
                      </label>
                      <div className="relative">
                        <select
                          id="service"
                          name="service_interest"
                          value={formData.service_interest}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF914D] focus:border-transparent transition-all appearance-none"
                        >
                          <option value="">Select a service</option>
                          <option value="social-media">Social Media Management</option>
                          <option value="branding">Brand Identity</option>
                          <option value="content">Content Creation</option>
                          <option value="website">Website Design</option>
                          <option value="digital-marketing">Digital Marketing</option>
                          <option value="training">Social Media Training</option>
                          <option value="other">Other</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-gray-500">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="message" className="block mb-2 text-sm font-medium text-gray-700">
                        Your Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={4}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF914D] focus:border-transparent transition-all resize-none"
                        placeholder="Tell us about your project..."
                      ></textarea>
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={formStatus.message && !formStatus.submitted && !formStatus.error}
                      className="w-full py-4 font-bold text-white transition-all bg-[#FF914D] rounded-xl hover:bg-[#FFB86B] shadow-lg shadow-orange-500/30 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      Send Message
                    </motion.button>
                  </form>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container">
          <h2 className="mb-12 text-4xl font-bold text-center font-display text-dark">Frequently Asked Questions</h2>

          <div className="max-w-3xl mx-auto space-y-4">
            {[
              {
                question: "How quickly can you start working on my project?",
                answer: "We typically begin new projects within 1-2 weeks of signing the contract. For urgent needs, we may be able to accommodate a faster timeline."
              },
              {
                question: "Do you offer ongoing support after the project is completed?",
                answer: "Yes, we offer various support packages to ensure your digital presence continues to thrive. We can discuss the best option for your needs during our consultation."
              },
              {
                question: "What information do you need from me to get started?",
                answer: "To get started, we'll need information about your business, target audience, goals, and any existing brand assets. We'll provide a detailed questionnaire to guide this process."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-xl overflow-hidden"
              >
                <details className="group">
                  <summary className="flex justify-between items-center font-medium cursor-pointer list-none p-6 text-lg text-dark hover:text-[#FF914D] transition-colors">
                    <span>{faq.question}</span>
                    <span className="transition group-open:rotate-180">
                      <svg fill="none" height="24" shapeRendering="geometricPrecision" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" viewBox="0 0 24 24" width="24"><path d="M6 9l6 6 6-6"></path></svg>
                    </span>
                  </summary>
                  <div className="text-gray-600 px-6 pb-6 leading-relaxed border-t border-gray-100 pt-4">
                    {faq.answer}
                  </div>
                </details>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;